import { FC, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import LoadingSpinner from './LoadingSpinner';

interface SeopRouteProps {
  children: React.ReactNode;
}

/**
 * Guard das rotas do Balanceamento de Linha (frente SEOP).
 *
 * O módulo não entra na Sidebar (B-D2) — chega-se nele por link direto —, então
 * este guard é a primeira coisa que um usuário sem liberação encontra. A checagem
 * é redundante com o backend (que devolve 403 estruturado); aqui é UX, para não
 * deixar o usuário bater na 403 sem entender por quê.
 *
 * A liberação é nominal: admin liga a flag no usuário ou no setor. ADMIN e
 * CURATOR passam sempre. `undefined` conta como bloqueado — fail-closed.
 */
const SeopRoute: FC<SeopRouteProps> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuthContext();
  const { showToast } = useToast();

  const blocked = !isLoading && isAuthenticated && user && !user.seop_feature_enabled;

  useEffect(() => {
    if (blocked) {
      showToast(
        'Balanceamento de Linha ainda não foi liberado pra você. Fale com seu admin.',
        'warning',
      );
    }
  }, [blocked, showToast]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-primary">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (blocked) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default SeopRoute;
